import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, brl } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { Truck, Home as HomeIcon, AlertTriangle, ChevronRight, Inbox } from "lucide-react";

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return "";
  }
};

function Dashboard({ user }) {
  const [quotes, setQuotes] = useState(null);
  const [filtro, setFiltro] = useState("todos");

  useEffect(() => {
    api.get("/admin/quotes").then((r) => setQuotes(r.data.quotes)).catch(() => setQuotes([]));
  }, []);

  const list = (quotes || []).filter((q) => {
    if (filtro === "todos") return true;
    if (filtro === "manual") return q.pricing?.manual_evaluation || q.manual_evaluation_override;
    return q.tipo === filtro;
  });

  return (
    <AdminShell user={user} title="Orçamentos">
      <div className="px-4 mt-4 flex gap-2" data-testid="admin-filters">
        {[
          { v: "todos", l: "Todos" },
          { v: "frete", l: "Frete" },
          { v: "mudanca", l: "Mudança" },
          { v: "manual", l: "Avaliação manual" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`filter-${f.v}`}
            onClick={() => setFiltro(f.v)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold transition ${
              filtro === f.v ? "bg-navy text-[#F5B301]" : "bg-white text-slate-500 border border-slate-200"
            }`}
          >
            {f.l}
          </button>
        ))}
      </div>

      <div className="px-4 mt-4 space-y-3" data-testid="quotes-list">
        {quotes === null && (
          <div className="flex justify-center py-16">
            <div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" />
          </div>
        )}
        {quotes !== null && list.length === 0 && (
          <div className="text-center py-16 text-slate-400" data-testid="quotes-empty">
            <Inbox className="w-10 h-10 mx-auto mb-2" />
            <p className="text-sm">Nenhum orçamento encontrado.</p>
          </div>
        )}
        {list.map((q) => (
          <Link
            key={q.quote_id}
            to={`/admin/orcamento/${q.quote_id}`}
            data-testid={`quote-row-${q.quote_id}`}
            className="block bg-white rounded-2xl border border-slate-100 shadow-sm p-4 active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                {q.tipo === "frete" ? <Truck className="w-4 h-4 text-navy" /> : <HomeIcon className="w-4 h-4 text-navy" />}
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {q.tipo === "frete" ? "Frete" : "Mudança"} · {fmtDate(q.created_at)}
                </span>
              </div>
              <ChevronRight className="w-4 h-4 text-slate-300" />
            </div>
            <p className="font-heading font-bold text-navy text-sm">{q.cliente?.nome || "Cliente"}</p>
            {(q.cliente_corridas ?? 0) > 2 && (
              <span className="inline-block mt-1 text-[10px] font-extrabold bg-[#F5B301] text-navy px-2 py-0.5 rounded-full" data-testid={`recorrente-${q.quote_id}`}>
                ⭐ CLIENTE RECORRENTE · {q.cliente_corridas} corridas
              </span>
            )}
            {(q.cliente_corridas ?? 0) > 0 && q.cliente_corridas <= 2 && (
              <p className="text-[10px] text-slate-400 mt-0.5">{q.cliente_corridas} corrida(s) finalizada(s)</p>
            )}
            <p className="text-xs text-slate-500 mt-0.5 truncate">
              {q.origin?.cidade || q.origin?.cep} → {q.destination?.cidade || q.destination?.cep}
              {q.stops?.length > 0 && ` · ${q.stops.length} parada(s)`}
            </p>
            <div className="flex items-center justify-between mt-2.5">
              <div className="flex gap-1.5">
                {(q.pricing?.manual_evaluation || q.manual_evaluation_override) && (
                  <span className="flex items-center gap-1 text-[10px] font-bold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                    <AlertTriangle className="w-3 h-3" /> MANUAL
                  </span>
                )}
                <span className="text-[10px] font-bold bg-slate-100 text-slate-500 px-2 py-0.5 rounded-full uppercase">
                  {q.status || "novo"}
                </span>
              </div>
              <span className="font-heading font-extrabold text-navy">{brl(q.final_value)}</span>
            </div>
          </Link>
        ))}
      </div>
    </AdminShell>
  );
}

export default function AdminDashboard() {
  return <AdminGate>{(user) => <Dashboard user={user} />}</AdminGate>;
}
